import type { FlowTemplate } from "../types";

/**
 * QP-150 multi-portfolio variant of {@link advisoryMultiStandardNO}.
 * Goal page keeps goal-level inputs (name + risk); the portfolio content is split across three
 * `portfolio` pages — Portefølje (inputs), Analyse, Prognose — each rendered once per portfolio.
 * `multiPortfolio` ships `true` — advisors can add multiple portfolios per goal.
 *
 * Public pages mirror the internal structure with two differences:
 * - Trapets Lookup is excluded (advisor-administered PEP/sanctions check)
 * - Sumsub Verification replaces it with liveness-only verification for the client
 */
export const advisoryMultiPortfolioStandardNO: FlowTemplate = {
  key: "advisory-multi-portfolio-standard-no",
  category: "advisory-multi",
  language: "nb_NO",
  name: "Rådgivning flere mål, flere porteføljer - Standard",
  description:
    "Full flermåls rådgivning med en dedikert porteføljeside per mål. Porteføljeinndata og analyse (X-Ray, prognose) ligger på porteføljesiden; kostnader og formuesplan er på instansnivå.",
  badges: ["MiFID II", "Flere mål", "Flere porteføljer", "X-Ray"],

  multiGoal: true,
  // Porteføljesiden fungerer med én portefølje per mål når false. Sett true for å aktivere
  // fanestripen for å legge til / fjerne porteføljer per mål.
  multiPortfolio: true,
  allowPublicStart: false,
  allowPublicPdfGen: false,
  allowPublicSigning: false,

  pages: [
    // === BEFORE GOALS (Global pages) ===
    {
      name: "Klassifisering",
      visibility: "internal",
      pageType: "global",
      globalPosition: "before_goals",
      enableAdvisorNotes: true,
      layouts: [
        {
          layoutType: "two_column",
          components: [
            {
              type: "client_classification",
              columnPosition: "left",
              config: {
                style: "border",
                title: "Kundeklassifisering",
                description:
                  "Svar på spørsmålene nedenfor for å fastsette din kundeklassifisering i henhold til MiFID II-regelverket.",
                conditionalDisplay: { enabled: false, sourceFieldId: "", operator: "equals" },
              },
            },
            {
              type: "product_assessment",
              columnPosition: "right",
              config: {
                style: "border",
                title: "Produktvurdering",
                description:
                  "Velg hvilke typer investeringer du har tidligere erfaring med. Dette hjelper oss med å anbefale passende produkter for din portefølje.",
                conditionalDisplay: { enabled: false, sourceFieldId: "", operator: "equals" },
              },
            },
          ],
        },
        {
          layoutType: "two_column",
          components: [
            {
              type: "trapets_lookup",
              columnPosition: "left",
              config: {
                style: "border",
                title: "PEP/Sanksjonssjekk",
                description:
                  "Verifiser kunden mot PEP (politisk eksponert person) og sanksjonsdatabaser.",
                conditionalDisplay: { enabled: false, sourceFieldId: "", operator: "equals" },
              },
            },
            {
              type: "portfolio_list",
              columnPosition: "right",
              config: {
                style: "border",
                title: "Porteføljer",
                description:
                  "Oversikt over investeringsporteføljene dine og deres betalingsplaner.",
                showAmount: true,
                showFrequency: true,
                showDates: true,
              },
            },
          ],
        },
      ],
      components: [
        {
          type: "sumsub_verification",
          config: {
            style: "border",
            title: "Identitetsverifisering",
            description:
              "Verifiser kundens identitet ved hjelp av dokument- og biometriske kontroller.",
            livenessVerification: false,
          },
        },
      ],
    },
    {
      name: "Finansiell Situasjon",
      visibility: "internal",
      pageType: "global",
      globalPosition: "before_goals",
      components: [
        {
          type: "financial_situation",
          config: {
            style: "none",
            title: "Finansiell situasjon",
            description:
              "Vennligst oppgi informasjon om din inntekt, eiendeler, gjeld og faste forpliktelser.",
            showIncome: true,
            showAssets: true,
            showLiabilities: true,
            showCommitments: true,
            showCapacity: true,
            showStabilityQuestion: true,
            showExpectingChangesQuestion: true,
            showCapacityClassification: true,
            showSummaryMetrics: true,
            conditionalDisplay: { enabled: false, sourceFieldId: "", operator: "equals" },
          },
        },
      ],
    },

    // === GOAL PAGE (goal-level inputs only) ===
    {
      name: "Mål",
      visibility: "internal",
      pageType: "goal",
      layouts: [
        {
          layoutType: "two_column",
          components: [
            {
              type: "goal_input",
              columnPosition: "left",
              config: {
                style: "border",
                title: "Måldetaljer",
                description: "Gi dette investeringsmålet et navn.",
              },
            },
            {
              type: "risk_assessment",
              columnPosition: "right",
              config: {
                style: "border",
                title: "Risikovurdering",
                description:
                  "Svar på noen spørsmål for å hjelpe oss med å forstå din investeringshorisont og risikotoleranse.",
              },
            },
          ],
        },
      ],
    },

    // === PORTFOLIO PAGES (per-portfolio inputs + analytics; each rendered once per portfolio) ===
    {
      name: "Portefølje",
      visibility: "internal",
      pageType: "portfolio",
      components: [
        {
          type: "portfolio_input",
          config: {
            style: "border",
            title: "Porteføljedetaljer",
            description:
              "Angi investeringsbeløp, tidsplan og porteføljetype for denne porteføljen.",
            showPortfolioType: true,
            allowedPortfolioTypes: ["model", "custom", "dynamic"],
            showDateInputs: true,
          },
        },
        {
          type: "custom_portfolio",
          config: {
            style: "border",
            title: "Tilpasset portefølje",
            description:
              "Bygg en tilpasset portefølje ved å velge fond og spesifisere investeringsbeløp.",
            showIsin: true,
            showProductType: true,
          },
        },
      ],
    },
    {
      name: "Analyse",
      visibility: "internal",
      pageType: "portfolio",
      layouts: [
        {
          layoutType: "two_column",
          components: [
            {
              type: "portfolio_display",
              columnPosition: "left",
              config: {
                style: "border",
                title: "Porteføljeallokering",
                description:
                  "Basert på din risikoprofil, her er din anbefalte porteføljeallokering.",
                showPortfolioName: true,
              },
            },
            {
              type: "asset_allocation",
              columnPosition: "right",
              config: {
                style: "border",
                title: "Aktivaallokering",
                description: "Hvordan investeringen din er fordelt på ulike aktivaklasser.",
                enableFollowupComparison: true,
                showChart: true,
                showTable: true,
              },
            },
            {
              type: "portfolio_xray",
              columnPosition: "right",
              config: {
                style: "border",
                title: "Aksjestil",
                description:
                  "Detaljert oversikt over porteføljens underliggende beholdninger og eksponeringer.",
                displayMode: "equity_style",
                enableFollowupComparison: true,
                showChart: true,
                showTable: false,
              },
            },
          ],
        },
        {
          layoutType: "tabs_block",
          config: {
            tabs: [
              { id: "tab-1", name: "Aktiva allokering" },
              { id: "tab-2", name: "Landeksponering" },
              { id: "tab-3", name: "Regioneksponering" },
              { id: "tab-4", name: "Aksjesektor" },
              { id: "tab-5", name: "Obligasjonssektor" },
              { id: "tab-6", name: "Holdings" },
            ],
          },
          components: [
            {
              type: "portfolio_xray",
              tabId: "tab-1",
              config: {
                style: "none",
                description:
                  "Detaljert oversikt over porteføljens underliggende beholdninger og eksponeringer.",
                displayMode: "asset_allocation",
                enableFollowupComparison: true,
                showTable: true,
                showChart: true,
                conditionalDisplay: { enabled: false, sourceFieldId: "", operator: "equals" },
              },
            },
            {
              type: "portfolio_xray",
              tabId: "tab-2",
              config: {
                style: "none",
                description:
                  "Detaljert oversikt over porteføljens underliggende beholdninger og eksponeringer.",
                displayMode: "country_exposure",
                enableFollowupComparison: true,
                showTable: true,
                showChart: true,
                conditionalDisplay: { enabled: false, sourceFieldId: "", operator: "equals" },
              },
            },
            {
              type: "portfolio_xray",
              tabId: "tab-3",
              config: {
                style: "none",
                description:
                  "Detaljert oversikt over porteføljens underliggende beholdninger og eksponeringer.",
                displayMode: "regional_exposure",
                enableFollowupComparison: true,
                showTable: true,
                showChart: true,
                conditionalDisplay: { enabled: false, sourceFieldId: "", operator: "equals" },
              },
            },
            {
              type: "portfolio_xray",
              tabId: "tab-4",
              config: {
                style: "none",
                description:
                  "Detaljert oversikt over porteføljens underliggende beholdninger og eksponeringer.",
                displayMode: "stock_sector",
                enableFollowupComparison: true,
                showChart: true,
                showTable: true,
                conditionalDisplay: { enabled: false, sourceFieldId: "", operator: "equals" },
              },
            },
            {
              type: "portfolio_xray",
              tabId: "tab-5",
              config: {
                style: "none",
                description:
                  "Detaljert oversikt over porteføljens underliggende beholdninger og eksponeringer.",
                displayMode: "bond_sector",
                enableFollowupComparison: true,
                showChart: true,
                showTable: true,
                conditionalDisplay: { enabled: false, sourceFieldId: "", operator: "equals" },
              },
            },
            {
              type: "portfolio_xray",
              tabId: "tab-6",
              config: {
                style: "none",
                description:
                  "Detaljert oversikt over porteføljens underliggende beholdninger og eksponeringer.",
                displayMode: "top_holdings",
                showChart: true,
                showTable: false,
                conditionalDisplay: { enabled: false, sourceFieldId: "", operator: "equals" },
              },
            },
          ],
        },
      ],
    },
    {
      name: "Prognose",
      visibility: "internal",
      pageType: "portfolio",
      layouts: [
        {
          layoutType: "two_column",
          components: [
            {
              type: "portfolio_forecast",
              columnPosition: "left",
              config: {
                style: "border",
                title: "Porteføljeprognose",
                description:
                  "Forventet porteføljeverdi over tid med 95% konfidensintervall basert på historiske markedsdata.",
                showStatistics: true,
                showTable: true,
              },
            },
            {
              type: "historical_return",
              columnPosition: "right",
              config: {
                style: "border",
                title: "Historisk avkastning",
                description:
                  "Historisk utvikling av porteføljen basert på tidligere markedsdata. Historisk avkastning er ingen garanti for fremtidig avkastning.",
              },
            },
          ],
        },
      ],
      components: [
        {
          type: "cost_benefit",
          config: {
            style: "border",
            title: "Kost/nytte",
            description:
              "Hvordan den foreslåtte porteføljen står seg mot din eksisterende på forventet avkastning, risiko og kostnader, og hva byttet koster én gang.",
          },
        },
      ],
    },

    // === AFTER GOALS (Global pages) ===
    {
      name: "Formuesplan",
      visibility: "internal",
      pageType: "global",
      globalPosition: "after_goals",
      layouts: [
        {
          layoutType: "two_column",
          components: [
            {
              type: "wealth_plan",
              columnPosition: "left",
              config: {
                style: "border",
                title: "Formuesplan",
                description:
                  "Planlegg fremtidige uttak fra investeringsporteføljen din for å nå dine økonomiske mål.",
              },
            },
            {
              type: "expected_value",
              columnPosition: "right",
              config: {
                style: "border",
                title: "Forventet verdi",
                description:
                  "Forventet porteføljeverdi over tid basert på forventet avkastning og din investeringsplan.",
                showChart: true,
                showSummaryTable: true,
                showYearlyTable: true,
              },
            },
          ],
        },
      ],
    },
    {
      name: "Kostnader",
      visibility: "internal",
      pageType: "global",
      globalPosition: "after_goals",
      layouts: [
        {
          layoutType: "two_column",
          components: [
            {
              type: "cost_chart",
              columnPosition: "left",
              config: {
                style: "border",
                title: "Kostnadseffekt",
                description:
                  "Visualisering av hvordan kostnader påvirker porteføljeverdien over tid.",
                displayMode: "rolling",
              },
            },
            {
              type: "cost_table",
              columnPosition: "right",
              config: {
                style: "border",
                title: "Kostnadstabell",
                description: "Oversikt over alle kostnader knyttet til investeringen din.",
              },
            },
            {
              type: "portfolio_costs",
              columnPosition: "right",
              config: {
                style: "border",
                title: "Rådgivningskostnader",
                description: "Plattform- og rådgivningskostnader for investeringsporteføljen din.",
              },
            },
            {
              type: "product_costs",
              columnPosition: "right",
              config: {
                style: "border",
                title: "Produktkostnader",
                description:
                  "Løpende kostnader for hvert produkt i porteføljen din, inkludert forvaltningshonorar og transaksjonskostnader.",
              },
            },
          ],
        },
      ],
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // OFFENTLIGE SIDER — klientvendt speiling av de interne sidene.
    // Forskjeller: ingen trapets_lookup; sumsub_verification med kun livlighetspåvisning.
    // ═══════════════════════════════════════════════════════════════════════════

    // === OFFENTLIGE FØR MÅL (Globale sider) ===
    {
      name: "Klassifisering",
      visibility: "public",
      pageType: "global",
      globalPosition: "before_goals",
      components: [
        {
          type: "client_classification",
          config: {
            style: "border",
            title: "Kundeklassifisering",
            description:
              "Svar på spørsmålene nedenfor for å fastsette din kundeklassifisering i henhold til MiFID II-regelverket.",
          },
        },
        {
          type: "product_assessment",
          config: {
            style: "border",
            title: "Produktvurdering",
            description:
              "Velg hvilke typer investeringer du har tidligere erfaring med. Dette hjelper oss med å anbefale passende produkter for din portefølje.",
          },
        },
        {
          type: "sumsub_verification",
          config: {
            style: "border",
            title: "Identitetsverifisering",
            description:
              "Fullfør identitetsverifisering ved å oppgi et gyldig ID-dokument og en selfie.",
            livenessVerification: true,
          },
        },
        {
          type: "portfolio_list",
          config: {
            style: "border",
            title: "Porteføljer",
            description: "Oversikt over investeringsporteføljene dine og deres betalingsplaner.",
            showAmount: true,
            showFrequency: true,
            showDates: true,
          },
        },
      ],
    },
    // === OFFENTLIG MÅLSIDE ===
    {
      name: "Mål",
      visibility: "public",
      pageType: "goal",
      components: [
        {
          type: "goal_input",
          config: {
            style: "border",
            title: "Måldetaljer",
            description: "Gi dette investeringsmålet et navn.",
          },
        },
        {
          type: "risk_assessment",
          config: {
            style: "border",
            title: "Risikovurdering",
            description:
              "Svar på noen spørsmål for å hjelpe oss med å forstå din investeringshorisont og risikotoleranse.",
          },
        },
        {
          type: "portfolio_list",
          config: {
            style: "border",
            title: "Porteføljer",
            description: "Oversikt over investeringsporteføljene dine og deres betalingsplaner.",
            showAmount: true,
            showFrequency: true,
            showDates: true,
          },
        },
      ],
    },

    // === OFFENTLIGE PORTEFØLJESIDER ===
    {
      name: "Portefølje",
      visibility: "public",
      pageType: "portfolio",
      components: [
        {
          type: "portfolio_input",
          config: {
            style: "border",
            title: "Porteføljedetaljer",
            description:
              "Angi investeringsbeløp, tidsplan og porteføljetype for denne porteføljen.",
            showPortfolioType: true,
            allowedPortfolioTypes: ["model", "custom", "dynamic"],
            showDateInputs: true,
          },
        },
        {
          type: "custom_portfolio",
          config: {
            style: "border",
            title: "Tilpasset portefølje",
            description:
              "Bygg en tilpasset portefølje ved å velge fond og spesifisere investeringsbeløp.",
            showIsin: true,
            showProductType: true,
          },
        },
      ],
    },
    {
      name: "Analyse",
      visibility: "public",
      pageType: "portfolio",
      components: [
        {
          type: "portfolio_display",
          config: {
            style: "border",
            title: "Porteføljeallokering",
            description: "Basert på din risikoprofil, her er din anbefalte porteføljeallokering.",
            showPortfolioName: true,
          },
        },
        {
          type: "asset_allocation",
          config: {
            style: "border",
            title: "Aktivaallokering",
            description: "Hvordan investeringen din er fordelt på ulike aktivaklasser.",
            enableFollowupComparison: true,
            showChart: true,
            showTable: true,
          },
        },
        {
          type: "portfolio_xray",
          config: {
            style: "border",
            title: "Aksjestil",
            description:
              "Detaljert oversikt over porteføljens underliggende beholdninger og eksponeringer.",
            displayMode: "equity_style",
            enableFollowupComparison: true,
            showChart: true,
            showTable: false,
          },
        },
        {
          type: "portfolio_xray",
          config: {
            style: "border",
            title: "X-Ray aktivaallokering",
            description:
              "Detaljert oversikt over porteføljens underliggende beholdninger og eksponeringer.",
            displayMode: "asset_allocation",
            enableFollowupComparison: true,
            showTable: true,
            showChart: true,
          },
        },
        {
          type: "portfolio_xray",
          config: {
            style: "border",
            title: "Landeksponering",
            description:
              "Detaljert oversikt over porteføljens underliggende beholdninger og eksponeringer.",
            displayMode: "country_exposure",
            enableFollowupComparison: true,
            showTable: true,
            showChart: true,
          },
        },
        {
          type: "portfolio_xray",
          config: {
            style: "border",
            title: "Regioneksponering",
            description:
              "Detaljert oversikt over porteføljens underliggende beholdninger og eksponeringer.",
            displayMode: "regional_exposure",
            enableFollowupComparison: true,
            showTable: true,
            showChart: true,
          },
        },
        {
          type: "portfolio_xray",
          config: {
            style: "border",
            title: "Aksjesektor",
            description:
              "Detaljert oversikt over porteføljens underliggende beholdninger og eksponeringer.",
            displayMode: "stock_sector",
            enableFollowupComparison: true,
            showChart: true,
            showTable: true,
          },
        },
        {
          type: "portfolio_xray",
          config: {
            style: "border",
            title: "Obligasjonssektor",
            description:
              "Detaljert oversikt over porteføljens underliggende beholdninger og eksponeringer.",
            displayMode: "bond_sector",
            enableFollowupComparison: true,
            showChart: true,
            showTable: true,
          },
        },
        {
          type: "portfolio_xray",
          config: {
            style: "border",
            title: "Beholdninger",
            description:
              "Detaljert oversikt over porteføljens underliggende beholdninger og eksponeringer.",
            displayMode: "top_holdings",
            showChart: true,
            showTable: false,
          },
        },
      ],
    },
    {
      name: "Prognose",
      visibility: "public",
      pageType: "portfolio",
      components: [
        {
          type: "portfolio_forecast",
          config: {
            style: "border",
            title: "Porteføljeprognose",
            description:
              "Forventet porteføljeverdi over tid med 95% konfidensintervall basert på historiske markedsdata.",
            showStatistics: true,
            showTable: true,
          },
        },
        {
          type: "historical_return",
          config: {
            style: "border",
            title: "Historisk avkastning",
            description:
              "Historisk utvikling av porteføljen basert på tidligere markedsdata. Historisk avkastning er ingen garanti for fremtidig avkastning.",
          },
        },
      ],
    },

    // === OFFENTLIGE ETTER MÅL (Globale sider) ===
    {
      name: "Formuesplan",
      visibility: "public",
      pageType: "global",
      globalPosition: "after_goals",
      components: [
        {
          type: "wealth_plan",
          config: {
            style: "border",
            title: "Formuesplan",
            description:
              "Planlegg fremtidige uttak fra investeringsporteføljen din for å nå dine økonomiske mål.",
          },
        },
        {
          type: "expected_value",
          config: {
            style: "border",
            title: "Forventet verdi",
            description:
              "Forventet porteføljeverdi over tid basert på forventet avkastning og din investeringsplan.",
            showChart: true,
            showSummaryTable: true,
            showYearlyTable: true,
          },
        },
      ],
    },
    {
      name: "Kostnader",
      visibility: "public",
      pageType: "global",
      globalPosition: "after_goals",
      components: [
        {
          type: "cost_chart",
          config: {
            style: "border",
            title: "Kostnadseffekt",
            description: "Visualisering av hvordan kostnader påvirker porteføljeverdien over tid.",
            displayMode: "rolling",
          },
        },
        {
          type: "cost_table",
          config: {
            style: "border",
            title: "Kostnadstabell",
            description: "Oversikt over alle kostnader knyttet til investeringen din.",
          },
        },
        {
          type: "portfolio_costs",
          config: {
            style: "border",
            title: "Rådgivningskostnader",
            description: "Plattform- og rådgivningskostnader for investeringsporteføljen din.",
          },
        },
        {
          type: "product_costs",
          config: {
            style: "border",
            title: "Produktkostnader",
            description:
              "Løpende kostnader for hvert produkt i porteføljen din, inkludert forvaltningshonorar og transaksjonskostnader.",
          },
        },
      ],
    },
  ],
};
